/**
 * Patch 35.3b — Batch selection state for one campaign pack.
 *
 * Selection is scoped per-pack via a plain React context so that
 * changing the active pack clears it automatically. The store never
 * mirrors the backend's eligibility rules — it simply holds the
 * user's chosen asset ids and enforces the ``MAX`` cap.
 */
import {
  createContext, useCallback, useContext, useMemo, useState,
  type PropsWithChildren,
} from "react";
import { BATCH_IMAGE_MAX } from "@/entities/ai-studio";

interface BatchSelection {
  activePackId: string | null;
  selectedIds: string[];
  isSelected: (assetId: string) => boolean;
  toggle: (assetId: string) => void;
  clear: () => void;
  setActivePack: (packId: string | null) => void;
  atMax: boolean;
  count: number;
  max: number;
}

const NOT_INITIALIZED: BatchSelection = {
  activePackId: null,
  selectedIds: [],
  isSelected: () => false,
  toggle: () => undefined,
  clear: () => undefined,
  setActivePack: () => undefined,
  atMax: false,
  count: 0,
  max: BATCH_IMAGE_MAX,
};

const Ctx = createContext<BatchSelection>(NOT_INITIALIZED);

export function BatchSelectionProvider({ children }: PropsWithChildren): JSX.Element {
  const [activePackId, setActivePackId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const setActivePack = useCallback((packId: string | null) => {
    setActivePackId((prev) => {
      if (prev !== packId) {
        // Changing packs always clears selection.
        setSelectedIds([]);
      }
      return packId;
    });
  }, []);

  const toggle = useCallback((assetId: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(assetId)) return prev.filter((id) => id !== assetId);
      if (prev.length >= BATCH_IMAGE_MAX) return prev;
      return [...prev, assetId];
    });
  }, []);

  const clear = useCallback(() => setSelectedIds([]), []);
  const isSelected = useCallback(
    (assetId: string) => selectedIds.includes(assetId),
    [selectedIds],
  );

  const value: BatchSelection = useMemo(() => ({
    activePackId,
    selectedIds,
    isSelected,
    toggle,
    clear,
    setActivePack,
    atMax: selectedIds.length >= BATCH_IMAGE_MAX,
    count: selectedIds.length,
    max: BATCH_IMAGE_MAX,
  }), [activePackId, selectedIds, isSelected, toggle, clear, setActivePack]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBatchSelection(): BatchSelection {
  return useContext(Ctx);
}

/** Patch 35.3b — safe blocker copy for founder-facing display. Never
 * leaks provider names, database fields, or internal error codes. */
export function safeBlockerCopy(blocker: string | null): string | null {
  if (!blocker) return null;
  const map: Record<string, string> = {
    not_approved: "Approve this prompt before rendering.",
    not_image_capability: "This isn't an image prompt.",
    not_head: "Only the current version can render.",
    superseded: "This is a superseded version.",
    archived: "This asset is archived.",
    already_rendered: "This prompt has already been rendered.",
    empty_prompt: "This prompt is empty. Edit before rendering.",
    in_error_state: "This prompt is in an error state.",
    lock_held: "This prompt is currently rendering.",
    budget_blocked: "Production capacity is temporarily unavailable.",
  };
  return map[blocker] ?? "This item can't be rendered right now.";
}
